
/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2017, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/View',
	'doT',
	'text!./templates/filterCollapsibleView.html',
	'../../widgets/livewidget/nls/StringResources'
], function(View, doT, filterCollapsibleView, stringResources) {

	class FilterContainerCollapseView extends View {
		get id() {
			return 'filter-dock-collapse-handle-container';
		}
		get events() {
			return {
				'primaryaction': 'onClick'
			};
		}

		/**
		 * Constructor
		 *
		 * @param {object} options
		 * @param {object} options.viewModel - view model
		 *
		 */
		constructor(options) {
			super(arguments);
			this.dashboard = options.dashboardApi;
			this._filterContainerView = this.dashboard.getFeature('.CanvasExtensions').getExtension('filterDock');
			this._isExpand = false;
			/** Currently extraOptions support the followig rules
			 * extraClassName: 'something'
			 * handleIcon: 'somethingIcon', default to undefined. Using inline svg
			 * showActiveIndicator: true/false default to true
			 */
			this.extraOptions = options.extraOptions;
			this.targetId = options.targetId || 'filterDockContainer';
			this.dashboard.on('filterDock:disable', this.disable, this);
			this.dashboard.on('filterDock:enable', this.enable, this);
			this.dashboard.on('filterDock:collapse', this.collapse, this);
			this.dashboard.on('filterDock:expand', this.expand, this);
			// TODO: dashboard.getFeature('internal') is a deprecated internal dashboard api.
			//		We should use API events when they support undo.
			this.canvas = this.dashboard.getFeature('Canvas');
			if (this.canvas.getPropertyNameList().indexOf('fillColor') > -1) {
				let boardModel = this.dashboard.getFeature('internal').getBoardModel();
				this.removeModelEventHandler = boardModel.on('change:layout', this._onChangeLayout, this);
				this._colorsFeature = this.dashboard.getFeature('Colors');
				let boardFillColor = this.canvas.getPropertyValue('fillColor');
				this._backgroundColorClass = 'fill-' + this._colorsFeature.getColorClassName(boardFillColor);
				this._applyBackgroundColor();
			} else {
				this._backgroundColorClass = '';
			}

			this.tooltipExpand = stringResources.get('expand');
			this.tooltipCollapse = stringResources.get('collapse');

			this.contentTemplateFn = function() {
				return doT.template(filterCollapsibleView)({ extraOptions: this.extraOptions });
			};
			this.featureChecker = this.dashboard.getGlassCoreSvc('.FeatureChecker');
		}

		_onChangeLayout(payload) {
			if(payload.eventName === 'change:fillColor'){
				let boardFillColor = this.canvas.getPropertyValue('fillColor');
				if(this.isExpanded()){
					this._backgroundColorClass = 'fill-' + this._colorsFeature.getColorClassName(boardFillColor);
				}else{
					this._removeBackgroundColor();
					this._backgroundColorClass = 'fill-' + this._colorsFeature.getColorClassName(boardFillColor);
					this._applyBackgroundColor();
				}
			}
		}

		_removeBackgroundColor() {
			this.$el.removeClass(this._backgroundColorClass);
		}

		_applyBackgroundColor() {
			this.$el.addClass(this._backgroundColorClass);
		}

		remove() {
			this.dashboard.off('filterDock:disable', this.disable, this);
			this.dashboard.off('filterDock:enable', this.enable, this);
			this.dashboard.off('filterDock:collapse', this.collapse, this);
			this.dashboard.off('filterDock:expand', this.expand, this);
			this.$('#filter-dock-collapse-handle-icon').off('click');
			this.removeModelEventHandler && this.removeModelEventHandler.remove();

			super.remove();
		}

		_getPageContextAPI () {
			return this.dashboard.getFeature('PageContextService').getPageContext();
		}

		_getFilterCount() {
			const filterEntries = [];
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const contents = [this.canvas.getContent(), ...this.canvas.getContent().getChildren()];
				contents.forEach(content => {
					filterEntries.push(...content.getFeature('DataFilters').getFilterList());
				});
			} else {
				const pageContextAPI = this._getPageContextAPI();
				filterEntries.push(...pageContextAPI.getPageContextItems({ origin: 'filter', _strictMatch: true }));
			}
			return filterEntries.length;
		}

		/**
		 * On click handler
		 */
		onClick() {
			if (this.isExpanded()) {
				this.dashboard.triggerDashboardEvent('filterDock:collapse');
			} else {
				this.dashboard.triggerDashboardEvent('filterDock:expand');
			}
		}

		disable() {
			this.$el.addClass('filter-dock-disabled');
			this._filterContainerView && this._filterContainerView.disable();
		}

		enable() {
			this.$el.removeClass('filter-dock-disabled');
			this._filterContainerView && this._filterContainerView.enable();
		}

		isExpanded() {
			return this._isExpand;
		}

		_toggleExpand(collapse) {
			this.$el.toggleClass('filter-dock-collapse-handle-container-expanded', collapse);
			this.$el.toggleClass('filter-dock-collapse-handle-container-collapsed', !collapse);
			var tooltip = collapse ? this.tooltipExpand : this.tooltipCollapse;
			this.$el.find('#filter-dock-collapse-handle-icon').attr('title', tooltip);
			this.$el.find('.filterDockCollapseHandle').attr('area-label', tooltip);
		}

		expand() {
			this._toggleExpand(false);
			this._filterContainerView.expand();
			this._isExpand = true;
			this._removeBackgroundColor();
			// @todo: remove the filterDock:expanded event when EPIC CADBLW-6693 is done
			this.dashboard.triggerDashboardEvent('filterDock:expanded');
		}

		collapse() {
			this._toggleExpand(true);
			this._filterContainerView.collapse();
			this._isExpand = false;
			this._applyBackgroundColor();
		}

		changeToConsumeMode() {
			this._filterContainerView.changeToConsumeMode();
			if (this._getFilterCount() === 0) {
				this.collapse();
			}
		}

		changeToEditMode() {
			if(!this.isExpanded()){
				this.expand();
			}
			this._filterContainerView.changeToAuthorMode();
		}

		/**
		 * Method to show/hide the collapsible view
		 *
		 * @param {boolean} state - TRUE to show and FALSE to hide
		 */
		show(state) {
			state = !!state;
			this.$el.toggle(state);
		}

		// TODO:
		// 1. move from lifeCycleManager to DashboardState
		// 2. we assume the FilterContainerView class is loaded before this class which is true but not necessary.
		//     It would be better if we remove the dependency.
		_setupLifeCycleHandlers() {
			this.dashboard.getDashboardSvc('.LifeCycleManager')
				.then(LifeCycleManager => {
					LifeCycleManager.registerLifeCycleHandler('post:mode.edit', this.changeToEditMode.bind(this));
					LifeCycleManager.registerLifeCycleHandler('post:mode.consume', this.changeToConsumeMode.bind(this));
				});
		}

		_shouldDisplayCollapsed() {
			const renderer = this.dashboard.getFeature('Renderer.internal');
			const dashboardState = this.dashboard.getFeature('DashboardState');
			// TODO: remove check for renderer when MobileLayout feature flag is removed
			return !dashboardState.getUiState().authoring && (this._getFilterCount() === 0 ||
				renderer && renderer.isMobile());
		}

		render() {
			this.$el.empty();
			this.$el.html(this.dotTemplate({}));
			this.$el.append(this.contentTemplateFn());
			if (this._shouldDisplayCollapsed()) {
				this.collapse();
			} else {
				this.expand();
			}
			this._setupLifeCycleHandlers();
		}
	}

	return FilterContainerCollapseView;
});
